import { useCallback, useEffect, useMemo, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  type RiskReductionMeasure,
  useUpdateRiskReductionMeasure,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { toRiskReductionMeasureUpdateInput } from './riskReductionMeasuresUtils'

type EditRiskReductionMeasureImplementationProofDialogProps = {
  isOpen: boolean
  onClose: () => void
  complianceId: string
  riskReductionMeasure: RiskReductionMeasure
}

export function EditRiskReductionMeasureImplementationProofDialog({
  isOpen,
  onClose,
  complianceId,
  riskReductionMeasure,
}: EditRiskReductionMeasureImplementationProofDialogProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const updateMutation = useUpdateRiskReductionMeasure(complianceId)

  const [noteDraft, setNoteDraft] = useState(
    riskReductionMeasure.implementationNote ?? ''
  )

  useEffect(() => {
    if (!isOpen) {
      return
    }
    setNoteDraft(riskReductionMeasure.implementationNote ?? '')
  }, [isOpen, riskReductionMeasure.implementationNote])

  const isSavingNote = updateMutation.isPending

  const hasNoteChanges = useMemo(() => {
    const trimmedDraft = noteDraft.trim()
    const currentNote = riskReductionMeasure.implementationNote?.trim() ?? ''
    return (
      trimmedDraft !== currentNote &&
      (trimmedDraft.length > 0 || currentNote.length > 0)
    )
  }, [noteDraft, riskReductionMeasure.implementationNote])

  const handleClose = useCallback(() => {
    if (isSavingNote) {
      return
    }
    onClose()
  }, [isSavingNote, onClose])

  const handleSubmit = () => {
    if (isSavingNote || !hasNoteChanges) {
      if (!hasNoteChanges) {
        handleClose()
      }
      return
    }

    const trimmedDraft = noteDraft.trim()

    updateMutation.mutate(
      {
        riskReductionMeasureId: riskReductionMeasure.id,
        ...toRiskReductionMeasureUpdateInput(riskReductionMeasure, {
          implementationNote: trimmedDraft.length > 0 ? trimmedDraft : null,
        }),
      },
      {
        onSuccess: () => {
          toast.success(t('riskReductionMeasuresLibraryPage.updateSuccess'))
          onClose()
        },
        onError: (error) => {
          toast.error(
            error.message ??
              t('riskReductionMeasuresLibraryPage.saveFailureUpdate')
          )
        },
      }
    )
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className='sm:max-w-lg' showCloseButton={!isSavingNote}>
        <DialogHeader>
          <DialogTitle>
            {t(
              'riskReductionMeasuresLibraryPage.rrmDetail.editImplementationProofDialog.title'
            )}
          </DialogTitle>
        </DialogHeader>

        <div className='space-y-2'>
          <Label htmlFor='rrm-edit-implementation-proof-note'>
            {t('riskReductionMeasuresLibraryPage.implementation.noteLabel')}
          </Label>
          <Textarea
            id='rrm-edit-implementation-proof-note'
            value={noteDraft}
            onChange={(event) => {
              setNoteDraft(event.target.value)
            }}
            disabled={isSavingNote}
            placeholder={t(
              'riskReductionMeasuresLibraryPage.implementation.notePlaceholder'
            )}
            rows={4}
          />
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={handleClose} disabled={isSavingNote}>
            {t('common:actions.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={isSavingNote || !hasNoteChanges}>
            {isSavingNote ? <Loader2 className='size-4 animate-spin' /> : null}
            {t('riskReductionMeasuresLibraryPage.formDialog.updateButton')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
